import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { initialAPIListValue } from "utils/constants";

import {
  createShipmentFile,
  deleteShipmentFile,
  getShipmentDetails,
  getShipments,
} from "api/shipment";
import { ConfirmDialog, Spacer, StatusLabel } from "components/Custom";
import { useLayoutDispatch, popupNotification } from "context/LayoutContext";
import { toBase64Handler } from "utils/base64";
import { formatBilling } from "utils/carrier";
import { timezoneFormat } from "utils/date";

import { Close, ExternalLink, Trash } from "icons";

import useStyles from "./drawerStyle";

let intervalID;

const DeleteFileModal = ({ file_id, onClose, onDelete }) => {
  const layoutDispatch = useLayoutDispatch();
  const [isLoading, setIsLoading] = useState(false);

  return (
    <ConfirmDialog
      open={isLoading || !!file_id}
      title="Delete File"
      children="Shipment file will be deleted?"
      confirmText="Delete"
      onConfirm={() => {
        setIsLoading(true);
        deleteShipmentFile({
          id: file_id,
          responseSetter: (res) => {
            setIsLoading(false);
            if (res.success) {
              onClose(true);
              onDelete();
              popupNotification({
                dispatch: layoutDispatch,
                status: "success",
                message: `Shipment file deleted.`,
              });
            } else {
              if (res.message) {
                popupNotification({
                  dispatch: layoutDispatch,
                  status: "error",
                  message: res.message,
                });
              }
            }
          },
        });
      }}
      isLoading={isLoading}
      onClose={() => onClose(false)}
    />
  );
};

export default function ShipmentDrawer({
  open,
  onClose,
  shipmentId,
  onFileDelete,
}) {
  const classes = useStyles();
  const [shipment, setShipment] = useState({});

  useEffect(() => {
    if (shipmentId) {
      getShipments({
        params: {
          id: shipmentId,
          with_details: true,
        },
        setFunc: (res) => {
          if (res?.items?.results?.length > 0) {
            setShipment(res.items.results[0]);
          }
        },
      });
    }
  }, [shipmentId]);

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      PaperProps={{
        className: classes.drawerPaper,
      }}
    >
      <div className={classes.root}>
        <div className={classes.title}>
          <div className={classes.header}>
            <Typography variant="span">{shipment?.order?.customer_id}</Typography>
          </div>
          <IconButton
            disableRipple={true}
            onClick={() => onClose(false)}
            className={classes.orange}
          >
            <Close color="#FFF" />
          </IconButton>
        </div>
        <FilesAccordion
          shipment={shipment}
          onClose={onClose}
          onFileDelete={onFileDelete}
        />
        <ShipmentAccordion shipment={shipment} />
        <ItemsAccordion shipmentId={shipment?.id} />
      </div>
    </Drawer>
  );
}

function FilesAccordion({ shipment, onClose, onFileDelete }) {
  const classes = useStyles();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const layoutDispatch = useLayoutDispatch();
  const [deleteId, setDeleteId] = useState(null);

  const handleFileChange = ({ target: { files } }) => {
    const uploaded = [...uploadedFiles];
    const filesArray = [...files];
    filesArray
      .filter(
        (file) => uploadedFiles.findIndex((f) => f.name === file.name) === -1,
      )
      .map((file) => {
        uploaded.push(file);
        return file;
      });
    setUploadedFiles(filesArray);
  };

  const handleSubmit = async () => {
    const files = await toBase64Handler(uploadedFiles);
    const fileNames = uploadedFiles.map((file) => file.name);
    const fileTypes = [];
    fileNames.forEach((name) => {
      if (name.toLowerCase().includes(".pdf")) {
        fileTypes.push("PDF");
      } else {
        fileTypes.push("EXCEL");
      }
    });
    setIsLoading(true);
    createShipmentFile({
      body: {
        files,
        fileNames,
        fileTypes,
        shipmentId: shipment?.id,
      },
      responseSetter: (res) => {
        if (res.success) {
          intervalID = setInterval(() => {
            createShipmentFile({
              body: {
                key: res.key,
              },
              responseSetter: (res) => {
                if (!res.success && res.message) {
                  setIsLoading(false);
                  popupNotification({
                    dispatch: layoutDispatch,
                    status: "error",
                    message: res.message,
                  });
                  return clearInterval(intervalID);
                } else if (res.success) {
                  setIsLoading(false);
                  onClose(true);
                  setUploadedFiles([]);
                  popupNotification({
                    dispatch: layoutDispatch,
                    status: "success",
                    message: `Shipment file uploaded.`,
                  });
                  return clearInterval(intervalID);
                }
              },
            });
          }, 5000);
        } else {
          if (res.message) {
            popupNotification({
              dispatch: layoutDispatch,
              status: "error",
              message: res.message,
            });
          }
          setIsLoading(false);
        }
      },
    });
  };

  useEffect(() => {
    if (uploadedFiles.length === 0) {
      return;
    }
    setIsLoading(true);
    handleSubmit();
  }, [uploadedFiles]);

  return (
    <Accordion expanded={true} className={classes.accordion}>
      <AccordionSummary
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        style={{ pointerEvents: "none" }}
      >
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography className={classes.heading}>Attachments</Typography>
          </Grid>
          <Grid item style={{pointerEvents: "auto"}}>
            <label htmlFor="file-upload">
              <input
                id="file-upload"
                type="file"
                accept="application/pdf, .xlsx, .xls"
                onChange={handleFileChange}
                multiple
                hidden
              />
              <Button
                variant="contained"
                size="medium"
                color="primary"
                component="span"
                disabled={isLoading}
              >
                {isLoading ? "Uploading..." : "Upload File"}
              </Button>
            </label>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          {shipment?.files?.map((file) => (
            <Grid item xs={12} className={classes.fileContainer} key={file.id}>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item>
                  <Tooltip
                    title={`${file.file_name} - ${timezoneFormat(
                      file.created_at,
                    ).slice(0, 8)}`}
                    aria-label="hello"
                  >
                    <Typography variant="body2" color="textPrimary">
                      {file.file_name.slice(0, 26)}
                      {file.file_name.length > 26
                        ? `...${file.file_name.split(".")[1].toUpperCase()}`
                        : ""}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => setDeleteId(file.id)}
                      >
                        <Trash color="red" size={0.8} />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => window.open(file.signed_url, "_blank")}
                      >
                        <ExternalLink size={0.8} color="green" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ))}
          {shipment?.files?.length > 0 && <Spacer height="1rem" />}
        </Grid>
      </AccordionDetails>
      <DeleteFileModal
        file_id={deleteId}
        onClose={() => setDeleteId(null)}
        onDelete={onFileDelete}
      />
    </Accordion>
  );
}

const LineItem = ({ title, value, complexItem = false }) => (
  <>
    <Grid item xs={7}>
      <Typography variant="subtitle2" color="textPrimary">
        {title}
      </Typography>
    </Grid>
    <Grid item xs={5}>
      {complexItem ? (
        <div style={{ float: "right" }}>{value}</div>
      ) : (
        <Typography
          variant="body2"
          align="right"
          color="textSecondary"
          style={{ fontWeight: "bold" }}
        >
          {value}
        </Typography>
      )}
    </Grid>
  </>
);

function ShipmentAccordion({ shipment }) {
  const classes = useStyles();
  const order = shipment?.order;
  const customer_name = shipment?.address?.customer;
  const phone = shipment?.address?.phone;

  return (
    <Accordion expanded={true}>
      <AccordionSummary
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        style={{ pointerEvents: "none" }}
      >
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography className={classes.heading}>Details</Typography>
          </Grid>
          <StatusLabel status={shipment?.status || ""} />
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1} style={{padding: "0 0 0 0.5rem"}}>
          <LineItem title="PO Number" value={order?.po_number} />
          <LineItem title="Billed To" value={formatBilling(shipment)} />
          <LineItem
            title="Delivery Address"
            value={
              <>
                {shipment?.address?.line_1} {shipment?.address?.line_2} <br />
                {shipment?.address?.city} {shipment?.address?.zip_code},{" "}
                {shipment?.address?.state} {shipment?.address?.country}
              </>
            }
          />
          <LineItem title="Contact Name" value={customer_name} />
          <LineItem title="Contact Phone" value={phone} />
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

function ItemCard({ item }) {
  return (
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
    >
      <Grid item>
        <Typography variant="body2" color="textPrimary">
          {item.product.sku}
        </Typography>
      </Grid>
      <Grid item>
        <Typography
          variant="body2"
          align="right"
          style={{ fontWeight: "semibold" }}
        >
          {item.quantity}
        </Typography>
      </Grid>
    </Grid>
  );
}

function ItemsAccordion({ shipmentId }) {
  const classes = useStyles();
  const [details, setDetails] = useState(initialAPIListValue);

  useEffect(() => {
    if (shipmentId) {
      setDetails(initialAPIListValue);
      getShipmentDetails({
        params: {
          shipment_id: shipmentId,
          limit: 9999,
        },
        setFunc: setDetails,
        paginated: true,
      });
    }
  }, [shipmentId]);

  return (
    <Accordion expanded={true}>
      <AccordionSummary
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        style={{ pointerEvents: "none" }}
      >
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography className={classes.heading}> Items </Typography>
          </Grid>
          {details?.items?.length ? (
            <Grid item>
              <Typography className={classes.heading}>
                {details?.items?.reduce(
                  (total, detail) => total + detail.quantity,
                  0,
                )}
              </Typography>
            </Grid>
          ) : null}
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        {details.isFetching ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={1} style={{padding: "0 0.25rem 0 0.75rem"}}>
            {details?.items?.map((row) =>
              row.product ? <ItemCard key={row.id} item={row} /> : null,
            )}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
